import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import styled from "styled-components";
import { apiConfig } from "../config/authConfig";

const ChatContainer = styled.div`
  width: 90%;
  max-width: none;
  margin: 1rem auto;
  padding: 0 1rem;
  font-family: "Roboto", "Arial", sans-serif;
  height: calc(100vh - 8rem);
  display: flex;
  flex-direction: row;
  gap: 1rem;
  overflow: hidden;
`;

const DocumentsSection = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  h3 {
    margin-top: 0;
    margin-bottom: 0.75rem;
    padding: 0;
    font-size: 1.05em;
  }
`;

const ChatSection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background-color: white;
`;

const Message = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser: boolean }>`
  display: flex;
  padding: 1.5rem 1rem;
  margin: 0;
  border-bottom: 1px solid #f1f1f1;
  background-color: ${(props) => (props.isUser ? "#f7f7f8" : "white")};

  &:last-child {
    border-bottom: none;
  }
`;

const MessageContent = styled.div<{ isUser: boolean }>`
  max-width: 100%;
  color: #374151;
  line-height: 1.6;
  font-size: 15px;
  white-space: pre-wrap;
  word-wrap: break-word;

  ${(props) =>
    props.isUser &&
    `
    font-weight: 500;
  `}
`;

const MessageIcon = styled.div<{ isUser: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 2px;
  margin-right: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  background-color: ${(props) => (props.isUser ? "#19c37d" : "#ab68ff")};
  color: white;
`;

const MainContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
  overflow: hidden;
`;

const InputContainer = styled.div`
  display: flex;
  padding: 1rem;
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e1dfdd;
  border-radius: 4px;
  margin-right: 0.5rem;

  &:focus {
    outline: none;
    border-color: #0078d4;
  }
`;

const SendButton = styled.button`
  background-color: #0078d4;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #106ebe;
  }

  &:disabled {
    background-color: #a19f9d;
    cursor: not-allowed;
  }
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  flex: 1;
`;

const DocumentTag = styled.span`
  background-color: #e1dfdd;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: #0078d4;
    color: white;
    transform: translateX(2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateX(0);
  }
`;

const SourcesContainer = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const SourcesTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const SourceItem = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 13px;
`;

const SourceHeader = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const SourceContent = styled.div`
  color: #6b7280;
  line-height: 1.4;
`;

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  sources?: Array<{
    content: string;
    source: string;
    page: string;
  }>;
}

interface Document {
  filename: string;
  path: string;
}

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingPDF, setLoadingPDF] = useState<string | null>(null);
  const { instance, accounts } = useMsal();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Function to scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Function to format AI responses for better readability
  const formatAIResponse = (text: string): string => {
    return text
      .replace(/\. /g, ".\n\n") // Add line breaks after sentences
      .replace(/\n\n+/g, "\n\n") // Remove excessive line breaks
      .replace(/:\s*([A-Z])/g, ":\n\n$1") // Add breaks after colons before capitals
      .replace(/(\d+\.)\s*/g, "\n$1 ") // Format numbered lists
      .trim();
  };

  // Function to open PDF in browser
  const openPDF = async (filename: string) => {
    try {
      setLoadingPDF(filename);
      const token = await getAccessToken();
      if (!token) {
        console.error("No access token available");
        alert("Authentication required. Please log in again.");
        return;
      }

      // Create a URL for the PDF endpoint
      const pdfUrl = `${apiConfig.baseUrl}/documents/${encodeURIComponent(
        filename,
      )}`;

      // Create a temporary link with authorization header by opening in new tab
      // We need to use a blob approach since we can't set headers for window.open
      const response = await fetch(pdfUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");

        // Clean up the blob URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        console.error("Failed to fetch PDF:", response.status);
        alert("Failed to open PDF. Please try again.");
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
      alert("Error opening PDF. Please try again.");
    } finally {
      setLoadingPDF(null);
    }
  };

  const getAccessToken = useCallback(async () => {
    try {
      console.log("Attempting to acquire token with custom API scope");
      console.log("Account:", accounts[0]?.username);

      const response = await instance.acquireTokenSilent({
        scopes: [
          `api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`,
        ],
        account: accounts[0],
      });
      console.log("Token acquired successfully");
      return response.accessToken;
    } catch (error) {
      console.error("Error getting access token:", error);
      // If silent token acquisition fails, try interactive
      try {
        console.log("Attempting interactive token acquisition...");
        const response = await instance.acquireTokenPopup({
          scopes: [
            `api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`,
          ],
          account: accounts[0],
        });
        console.log("Interactive token acquired successfully");
        return response.accessToken;
      } catch (popupError) {
        console.error("Interactive token acquisition failed:", popupError);
        return null;
      }
    }
  }, [instance, accounts]);

  const loadDocuments = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No access token");

      console.log("Making request to:", `${apiConfig.baseUrl}/documents`);
      console.log("Token (first 20 chars):", token.substring(0, 20) + "...");

      const response = await axios.get(`${apiConfig.baseUrl}/documents`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDocuments(response.data.documents);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(
          "Axios error:",
          error.response?.status,
          error.response?.data,
        );
        console.error("Request headers:", error.config?.headers);
      } else {
        console.error("Error loading documents:", error);
      }
    }
  }, [getAccessToken]);

  useEffect(() => {
    // Only load documents if user is authenticated
    if (accounts.length > 0) {
      loadDocuments();
    }
  }, [accounts, loadDocuments]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No access token");

      const response = await axios.post(
        `${apiConfig.baseUrl}/ask-question`,
        { question: inputValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const botMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: formatAIResponse(response.data.answer),
        isUser: false,
        sources: response.data.sources,
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I encountered an error processing your question. Please try again.",
        isUser: false,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <ChatContainer>
      <DocumentsSection>
        <h3>📚 Policies ({documents.length})</h3>
        <DocumentList>
          {[...documents]
            .sort((a, b) => a.filename.localeCompare(b.filename))
            .map((doc, index) => (
              <DocumentTag
                key={index}
                onClick={() => openPDF(doc.filename)}
                title={`Click to open ${doc.filename}`}
                style={{
                  opacity: loadingPDF === doc.filename ? 0.7 : 1,
                  cursor: loadingPDF === doc.filename ? "wait" : "pointer",
                }}
              >
                {doc.filename}
              </DocumentTag>
            ))}
          {documents.length === 0 && (
            <p style={{ color: "#605e5c", fontStyle: "italic", margin: 0 }}>
              No documents loaded.
            </p>
          )}
        </DocumentList>
      </DocumentsSection>
      <MainContentSection>
        {/* Disclaimer: AI may make mistakes */}
        <div
          style={{
            background: "#fffbe6",
            color: "#856404",
            border: "1px solid #ffeeba",
            borderRadius: "4px",
            padding: "8px",
            fontSize: "0.9em",
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          Disclaimer: This AI-powered chatbot may make mistakes. Please verify
          important information independently.
        </div>

        <ChatSection>
          <MessagesContainer>
            {messages.length === 0 && (
              <Message isUser={false}>
                {`Hello${
                  accounts && accounts[0]?.name
                    ? " " + accounts[0].name.split(" ")[0]
                    : ""
                }! Ask me questions about the policies and I'll help you find answers.`}
              </Message>
            )}
            {messages.map((message) => (
              <Message key={message.id} isUser={message.isUser}>
                <MessageIcon isUser={message.isUser}>
                  {message.isUser ? "U" : "AI"}
                </MessageIcon>
                <div style={{ flex: 1 }}>
                  <MessageContent isUser={message.isUser}>
                    {message.isUser
                      ? message.text
                      : formatAIResponse(message.text)}
                  </MessageContent>

                  {message.sources && message.sources.length > 0 && (
                    <SourcesContainer>
                      <SourcesTitle>Sources:</SourcesTitle>
                      {message.sources.map((source, idx) => (
                        <SourceItem key={idx}>
                          <SourceHeader>
                            {source.source} - Page {source.page}
                          </SourceHeader>
                          <SourceContent>{source.content}</SourceContent>
                        </SourceItem>
                      ))}
                    </SourcesContainer>
                  )}
                </div>
              </Message>
            ))}
            {isLoading && (
              <Message isUser={false}>
                <MessageIcon isUser={false}>AI</MessageIcon>
                <div style={{ flex: 1 }}>
                  <MessageContent isUser={false}>Thinking...</MessageContent>
                </div>
              </Message>
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>
          <InputContainer>
            <Input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask a question about the documents..."
              disabled={isLoading}
            />
            <SendButton
              onClick={sendMessage}
              disabled={isLoading || !inputValue.trim()}
            >
              Send
            </SendButton>
          </InputContainer>
        </ChatSection>
      </MainContentSection>
    </ChatContainer>
  );
};

export default ChatInterface;
